import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ForumIcon from '@mui/icons-material/Forum';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState, ActionMenu, ModalChatBody } from '../../components/shared';
import PortalSelfFulfillModal from './PortalSelfFulfillModal';
import { partOrders, portalTicketMessages } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';import ContentCard from "../../components/shared/ContentCard";

const STATUS_TABS = [
{ label: 'All', value: 'All' },
{ label: 'Open', value: 'Open' },
{ label: 'Fulfilled', value: 'Fulfilled' },
{ label: 'Shipped', value: 'Shipped' },
{ label: 'Completed', value: 'Completed' }];

// ─── Chat helpers ─────────────────────────────────────────────────────────────
function ChatBubbleIcon({ messages, onClick }) {
  const last = messages[messages.length - 1];
  const color = messages.length === 0 ?
  'var(--grey-300)' :
  last.sender === 'coordinator' ? '#1565C0' : '#009688';
  const tooltip = messages.length === 0 ?
  'No messages yet — click to chat' :
  `${last.senderName}: ${last.text.slice(0, 60)}${last.text.length > 60 ? '…' : ''}`;
  return (
    <Tooltip title={tooltip} placement="top" arrow enterDelay={200}>
      <ForumIcon
        onClick={onClick}
        sx={{ fontSize: 17, color, display: 'block', cursor: 'pointer', '&:hover': { opacity: 0.75 } }} />
      
    </Tooltip>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PortalTicketsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [tickets, setTickets] = useState(() =>
  partOrders.filter((o) =>
  o.customerName === currentUser.customerName ||
  o.resellerName === currentUser.customerName
  )
  );
  const [tabFilter, setTabFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [chatRow, setChatRow] = useState(null);
  const [messagesMap, setMessagesMap] = useState(() => {
    const map = {};
    portalTicketMessages.forEach((m) => {
      if (!map[m.partOrderId]) map[m.partOrderId] = [];
      map[m.partOrderId].push(m);
    });
    return map;
  });

  const chatMessages = chatRow ? messagesMap[chatRow.id] ?? [] : [];

  const handleChatSend = (text) => {
    if (!chatRow) return;
    const msg = { id: `portal-msg-${Date.now()}`, partOrderId: chatRow.id, sender: 'contact', senderName: currentUser.name ?? 'You', text, sentAt: new Date().toISOString() };
    setMessagesMap((prev) => ({ ...prev, [chatRow.id]: [...(prev[chatRow.id] ?? []), msg] }));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = tabFilter === 'All' ? tickets : tickets.filter((t) => t.status === tabFilter);
    if (!q) return base;
    return base.filter((t) =>
    t.ticketNumber.toLowerCase().includes(q) ||
    t.partNumber.toLowerCase().includes(q) ||
    (t.partDescription ?? '').toLowerCase().includes(q)
    );
  }, [tickets, tabFilter, search]);

  const handleFulfillDone = (updatedOrder) => {
    setTickets((prev) => prev.map((t) => t.id === updatedOrder.id ? { ...updatedOrder } : t));
    setFulfillTarget(null);
  };

  const columns = [
  {
    field: 'ticketNumber',
    headerName: 'Ticket #',
    sortable: true,
    renderCell: (row) =>
    <Typography
      onClick={() => navigate(`/portal/tickets/${row.id}`)}
      sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
          {row.ticketNumber}
        </Typography>

  },
  { field: 'partNumber', headerName: 'Part #', sortable: true },
  {
    field: 'resellerName',
    headerName: 'Reseller',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: row.resellerName ? 'var(--grey-800)' : 'var(--grey-400)' }}>
          {row.resellerName ?? '—'}
        </Typography>

  },
  {
    field: 'customerName',
    headerName: 'End-Customer',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
          {row.customerName}
        </Typography>

  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  { field: 'createdDate', headerName: 'Created', sortable: true },
  { field: 'sla', headerName: 'SLA', sortable: true },
  {
    field: '_chat',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
          <ChatBubbleIcon
        messages={messagesMap[row.id] ?? []}
        onClick={() => setChatRow(row)} />
      
        </Box>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <Box onClick={(e) => e.stopPropagation()}>
          <ActionMenu
        hideUntilRowHover
        actions={[
        { label: 'View Details', onClick: () => navigate(`/portal/tickets/${row.id}`) },
        ...(row.status === 'Open' ?
        [{ label: 'Self Fulfill', onClick: () => setFulfillTarget(row) }] :
        [])]
        } />
      
        </Box>

  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BreadcrumbHeader
        title="My Tickets"
        crumbs={[{ label: 'Portal' }, { label: 'My Tickets' }]} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={STATUS_TABS} value={tabFilter} onChange={setTabFilter} />

        <TextField
          fullWidth
          placeholder="Search by Ticket #, Part #, or Description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'var(--grey-400)' }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'var(--grey-200)' },
              '&:hover fieldset': { borderColor: 'var(--grey-400)' },
              '&.Mui-focused fieldset': { borderColor: '#009688' }
            }
          }} />

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <Box sx={{ p: 3 }}>
              <EmptyState title="No tickets found" description="No tickets match the selected filter." />
            </Box> :

          <DataTable columns={columns} data={filtered} />
          }
        </Box>
      </ContentCard>

      <PortalSelfFulfillModal
        open={Boolean(fulfillTarget)}
        order={fulfillTarget}
        onClose={() => setFulfillTarget(null)}
        onFulfilled={handleFulfillDone} />

      {/* Chat modal */}
      <Dialog
        open={Boolean(chatRow)}
        onClose={(e, reason) => {if (reason !== 'backdropClick') setChatRow(null);}}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}>
        
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
              Messages — {chatRow?.ticketNumber}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
              Logistics Support
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setChatRow(null)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', px: 0, pb: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ModalChatBody messages={chatMessages} onSend={handleChatSend} />
        </DialogContent>
      </Dialog>
    </Box>);

}